class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      searchQuery: "[data-query]",
      searchMessage: "[data-message]",
      input: "[data-search-input], input[type='search'], input[name='q']",
      submit: 'button[type="submit"]',
      loading: "[data-spinner]",
      clear: "[data-clear-search]",
      popularSearchItems: ["[data-ps-item]"],
      moreResultIcon: "[data-more-result-icon]",
    };

    this.container = this.closest("[data-search-popup]") || this;
    this.domNodes = queryDomNodes(this.selectors, this.container);

    this.cachedResults = {};
    this.transitionDuration = 300;
    this.input = this.querySelector('input[type="search"]') || this.querySelector('input[name="q"]') || this.querySelector("[data-search-input]");
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.searchPopupSearults = this.querySelector(".m-search-popup__result");
    this.popularSearches = this.container.querySelector("[data-popular-searches]");
    this.searchCount = this.container.querySelector("[data-search-count]");

    this.currentCategory = "all";
    this.currentSort = "relevance";

    this.setupEventListeners();
  }

  setPopularSearchesLink = () => {
    const { popularSearchItems } = this.domNodes;
    if (popularSearchItems && popularSearchItems.length) {
      popularSearchItems.forEach((itm) => (itm.href = createSearchLink(itm.dataset.psQuery)));
    }
  };

  setupEventListeners() {
    const clear = this.querySelector("[data-clear-search]");
    const form = this.querySelector("form");
    if (form) {
      form.addEventListener("submit", this.onFormSubmit.bind(this));
    }

    if (this.input) {
      this.input.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );

      this.input.addEventListener("focus", () => {
        if (this.getQuery().length && this.getAttribute("results") === "true") {
          this.open();
        }
      });
    }

    if (clear) {
      clear.addEventListener("click", this.onClearSearch.bind(this));
    }

    document.addEventListener("click", (e) => {
      if (!this.contains(e.target)) {
        this.close();
      }
    });

    MinimogEvents.subscribe(MinimogTheme.pubSubEvents.closeSearchPopup, () => this.close(true));
  }

  getQuery() {
    return this.input ? this.input.value.trim() : "";
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onClearSearch(event) {
    event.preventDefault();
    if (this.input) {
      this.input.value = "";
    }
    this.onChange();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, "-").toLowerCase();
    this.toggleSpinnerLoading(true);
    this.toggleClearSearch(false);

    const searchByTag = this.dataset.searchByTag === "true";
    const searchByBody = this.dataset.searchByBody === "true";
    const unavailableProductsOption = this.dataset.unavailableProductsOption || "last";

    let searchFields = "title,product_type,vendor,variants.sku,variants.title";

    if (searchByTag) searchFields += ",body";
    if (searchByBody) searchFields += ",tag";

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm);
      return;
    }

    const basePredictiveUrl =
      (window.MinimogSettings && window.MinimogSettings.routes && window.MinimogSettings.routes.predictive_search_url) ||
      (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'search/suggest' : '/search/suggest');

    let searchURL = `${basePredictiveUrl}?q=${encodeURIComponent(
      searchTerm
    )}&resources[type]=product,collection,page,article,query&resources[options][unavailable_products]=${unavailableProductsOption}&resources[options][fields]=${searchFields}&section_id=predictive-search`;

    fetch(`${searchURL}`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const parsedDoc = new DOMParser().parseFromString(text, "text/html");
        let resultsMarkup = "";
        const sectionElem =
          parsedDoc.querySelector("#shopify-section-predictive-search") ||
          parsedDoc.querySelector("[data-search-items-wrapper]") ||
          parsedDoc.querySelector(".m-search-result") ||
          parsedDoc.querySelector(".shopify-section") ||
          parsedDoc.body;

        if (sectionElem) {
          resultsMarkup = sectionElem.innerHTML || text;
        } else {
          resultsMarkup = text;
        }

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup, searchTerm);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  renderSearchResults(resultsMarkup, searchTerm) {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
    }
    this.setAttribute("results", true);
    const searchItemsWrapper = this.querySelector("[data-search-items-wrapper]");
    if (searchItemsWrapper && searchItemsWrapper.childElementCount > 0) {
      this.renderSearchQueryAndMessage(true);
      this.initInteractiveFeatures(searchTerm);
    } else {
      this.renderSearchQueryAndMessage(false);
    }

    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);
    this.open();
  }

  initInteractiveFeatures(searchTerm) {
    // 1. Reset state
    this.currentCategory = "all";
    this.currentSort = "relevance";

    // 2. Keyword Highlighting
    this.highlightKeywords(searchTerm);

    // 3. Category Filter Tabs Click
    const catTabs = this.querySelectorAll("[data-category-filter]");
    catTabs.forEach((tab) => {
      tab.addEventListener("click", (e) => {
        e.preventDefault();
        catTabs.forEach((t) => t.classList.remove("active"));
        tab.classList.add("active");
        this.currentCategory = tab.dataset.categoryFilter;
        this.applyFiltersAndSort();
      });
    });

    // 4. Sorting Bar Click
    const sortTabs = this.querySelectorAll("[data-sort-by]");
    sortTabs.forEach((tab) => {
      tab.addEventListener("click", (e) => {
        e.preventDefault();
        sortTabs.forEach((t) => t.classList.remove("active"));
        tab.classList.add("active");
        this.currentSort = tab.dataset.sortBy;
        this.applyFiltersAndSort();
      });
    });
  }

  highlightKeywords(searchTerm) {
    if (!searchTerm) return;
    const titleElements = this.querySelectorAll("[data-original-title]");
    const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedTerm})`, "gi");

    titleElements.forEach((el) => {
      const originalText = el.dataset.originalTitle || el.textContent;
      if (regex.test(originalText)) {
        el.innerHTML = originalText.replace(regex, '<mark class="m-search-highlight">$1</mark>');
      } else {
        el.textContent = originalText;
      }
    });
  }

  applyFiltersAndSort() {
    const container = this.querySelector("[data-product-items]");
    if (!container) return;

    const rows = Array.from(this.querySelectorAll("[data-search-row]"));

    // Filter
    rows.forEach((row) => {
      const rowCat = (row.dataset.category || "").trim();
      if (this.currentCategory === "all" || rowCat === this.currentCategory) {
        row.style.display = "flex";
      } else {
        row.style.display = "none";
      }
    });

    // Sort
    const visibleRows = rows.filter((row) => row.style.display !== "none");

    visibleRows.sort((a, b) => {
      if (this.currentSort === "price-asc") {
        const priceA = parseFloat(a.dataset.price) || 0;
        const priceB = parseFloat(b.dataset.price) || 0;
        return priceA - priceB;
      } else if (this.currentSort === "price-desc") {
        const priceA = parseFloat(a.dataset.price) || 0;
        const priceB = parseFloat(b.dataset.price) || 0;
        return priceB - priceA;
      } else {
        // relevance / default
        const indexA = parseInt(a.dataset.index) || 0;
        const indexB = parseInt(b.dataset.index) || 0;
        return indexA - indexB;
      }
    });

    // Re-append sorted visible rows
    visibleRows.forEach((row) => container.appendChild(row));
  }

  renderSearchQueryAndMessage(results) {
    const searchQuery = this.container.querySelector("[data-query]");
    const searchMessage = this.container.querySelector("[data-message]");
    const moreResultIcon = this.container.querySelector("[data-more-result-icon]");
    const query = this.input ? this.input.value : "";

    if (searchQuery) searchQuery.textContent = query;

    if (this.predictiveSearchResults) this.predictiveSearchResults.classList.remove("m:hidden");
    if (this.searchPopupSearults) {
      this.searchPopupSearults.classList.remove("m:hidden");
      this.searchPopupSearults.style.display = "block";
    }

    if (searchMessage) {
      if (results) {
        searchMessage.textContent = searchMessage.dataset.resultsTitle || "Results for";
        if (moreResultIcon) moreResultIcon.classList.add("m:hidden");
      } else {
        searchMessage.textContent = searchMessage.dataset.resultsTitle || "No results for";
        if (moreResultIcon) moreResultIcon.classList.remove("m:hidden");
      }
    }
  }

  toggleSpinnerLoading(show) {
    const submit = this.querySelector('button[type="submit"]');
    const loading = this.querySelector("[data-spinner]");
    if (submit) submit.style.visibility = show ? "hidden" : "visible";
    if (loading) loading.style.display = show ? "inline-flex" : "none";
  }

  toggleClearSearch(show) {
    const clear = this.querySelector("[data-clear-search]");
    if (clear) clear.style.display = show ? "inline-flex" : "none";
  }

  open() {
    if (this.searchPopupSearults) {
      this.searchPopupSearults.classList.remove("m:hidden");
      this.searchPopupSearults.style.display = "block";
      this.searchPopupSearults.style.setProperty(
        "--search-result-max-height",
        `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`
      );
    }
    this.setAttribute("open", true);
    if (this.input) this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;

    if (this.popularSearches) this.popularSearches.classList.add("m:hidden");
    if (this.searchCount) this.searchCount.classList.remove("m:hidden");
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      if (this.input) this.input.value = "";
      this.removeAttribute("results");
      this.toggleClearSearch(false);
      this.resultsMaxHeight = false;
    }

    this.removeAttribute("open");
    if (this.predictiveSearchResults) this.predictiveSearchResults.classList.add("m:hidden");
    if (this.searchPopupSearults) {
      this.searchPopupSearults.classList.add("m:hidden");
      this.searchPopupSearults.style.display = "none";
    }
    if (this.popularSearches) this.popularSearches.classList.remove("m:hidden");
    if (this.searchCount) this.searchCount.classList.add("m:hidden");
  }

  getResultsMaxHeight() {
    if (this.getBoundingClientRect) {
      this.resultsMaxHeight = window.innerHeight - this.getBoundingClientRect().bottom - 20;
      return Math.max(350, this.resultsMaxHeight);
    }
    return 500;
  }
}

customElements.define("predictive-search", PredictiveSearch);
